/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */

const bookingLimit = {
  id: 'bookingLimit',
  label: 'Filter.bookingLimit',
  type: 'SelectMultipleFilter',
  group: 'secondary',
  queryParamNames: ['pub_bookingLimit'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: 'limit_1', label: 'Filter.bookingLimit.limit_1' },
      { key: 'limit_2', label: 'Filter.bookingLimit.limit_2' },
      { key: 'limit_3', label: 'Filter.bookingLimit.limit_3' },
      { key: 'limit_4', label: 'Filter.bookingLimit.limit_4' },
      { key: 'limit_bigger_4', label: 'Filter.bookingLimit.limit_bigger_4' },
    ],
  },
};

const bookingMinimum = {
  id: 'bookingMinimum',
  label: 'Filter.bookingMinimum',
  type: 'SelectMultipleFilter',
  group: 'secondary',
  queryParamNames: ['pub_bookingMinimum'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: 'min_1', label: 'Filter.bookingMinimum.min_1' },
      { key: 'min_2', label: 'Filter.bookingMinimum.min_2' },
      { key: 'min_3', label: 'Filter.bookingMinimum.min_3' },
      { key: 'min_4', label: 'Filter.bookingMinimum.min_4' },
      { key: 'min_5', label: 'Filter.bookingMinimum.min_5' },
      { key: 'min_6', label: 'Filter.bookingMinimum.min_6' },
      { key: 'min_7', label: 'Filter.bookingMinimum.min_7' },
    ],
  },
};

const dateFilter = {
  id: 'dates',
  label: 'Filter.dates',
  type: 'BookingDateRangeFilter',
  group: 'primary',
  // Note: BookingDateRangeFilter is fixed filter,
  // you can't change "queryParamNames: ['dates'],"
  queryParamNames: ['dates'],
  config: {},
};

const bookingCategoryFilter = {
  id: 'category',
  label: 'Filter.category',
  type: 'SelectSingleFilter',
  group: 'secondary',
  queryParamNames: ['pub_category'],
  config: {
    // "key" is the option you see in Flex Console.
    // "label" is set here for the UI only.
    // Note: label is not added through the translation files
    // to make filter customizations a bit easier.
    options: [
      { key: 'bookingRequest', label: 'Filter.category.bookingRequest', icon: 'IconBookings' },
      {
        key: 'expressBooking',
        label: 'Filter.category.expressBooking',
        icon: 'IconExpressBooking',
      },
    ],
  },
};

const verifiedFilter = {
  id: 'verified',
  label: 'Filter.verified',
  type: 'SelectSingleFilter',
  group: 'secondary',
  queryParamNames: ['pub_verified'],
  config: {
    // "key" is the option you see in Flex Console.
    // "label" is set here for the UI only.
    // Note: label is not added through the translation files
    // to make filter customizations a bit easier.
    options: [{ key: '1', label: 'Filter.verified', icon: 'IconVerified' }],
  },
};

const noPlusListingFilter = {
  id: 'noPlusListing',
  label: 'Filter.noPlusListing',
  type: 'SelectSingleFilter',
  group: 'secondary',
  queryParamNames: ['no_plus_listing'],
  config: {
    options: [{ key: '1', label: 'Filter.noPlusListing', icon: 'IconPlus' }],
  },
};

const allWheelDriveFilter = {
  id: 'allWheelDrive',
  label: 'Filter.allWheelDrive',
  type: 'SelectSingleFilter',
  group: 'secondary',
  queryParamNames: ['pub_allWheelDrive'],
  config: {
    options: [
      { key: 'yes', label: 'Filter.allWheelDrive.yes' },
      {
        key: 'no',
        label: 'Filter.allWheelDrive',
        icon: 'Surroundings/IconSurroundingsMountain',
      },
    ],
  },
};

const listingCategoryFilter = {
  id: 'listingCategory',
  label: 'Filter.listingCategory',
  type: 'SelectMultipleFilter',
  group: 'primary',
  queryParamNames: ['pub_listingCategory'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: 'campingPitch', label: 'Filter.listingCategory.campingPitch' },
      { key: 'campingGround', label: 'Filter.listingCategory.campingGround' },
    ],
  },
};

const amenitiesFilter = {
  id: 'amenities',
  label: 'Filter.amenities',
  type: 'SelectMultipleFilter',
  group: 'primary',
  queryParamNames: ['pub_amenities'],
  config: {
    // Optional modes: 'has_all', 'has_any'
    // https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
    searchMode: 'has_all',

    // "key" is the option you see in Flex Console.
    // "label" is set here for this web app's UI only.
    // Note: label is not added through the translation files
    // to make filter customizations a bit easier.
    options: [
      {
        key: 'electricity',
        label: 'Filter.amenities.electricity',
        icon: 'Amenities/IconAmenitiesElectricity',
      },
      { key: 'water', label: 'Filter.amenities.water', icon: 'Amenities/IconAmenitiesWater' },
      {
        key: 'shower',
        label: 'Filter.amenities.shower',
        icon: 'Amenities/IconAmenitiesShower',
      },
      { key: 'wc', label: 'Filter.amenities.wc', icon: 'Amenities/IconAmenitiesWC' },
      { key: 'dog', label: 'Filter.amenities.dog', icon: 'Amenities/IconAmenitiesDog' },
      { key: 'wifi', label: 'Filter.amenities.wifi', icon: 'Amenities/IconAmenitiesWiFi' },
      {
        key: 'fireplace',
        label: 'Filter.amenities.fireplace',
        icon: 'Amenities/IconAmenitiesFireplace',
      },
      {
        key: 'greywater',
        label: 'Filter.amenities.greywater',
        icon: 'Amenities/IconAmenitiesGreyWater',
      },
      {
        key: 'blackwater',
        label: 'Filter.amenities.blackwater',
        icon: 'Amenities/IconAmenitiesBlackwater',
      },
      {
        key: 'wascher',
        label: 'Filter.amenities.wascher',
        icon: 'Amenities/IconAmenitiesWascher',
      },
      {
        key: 'garbage',
        label: 'Filter.amenities.garbage',
        icon: 'Amenities/IconAmenitiesGarbage',
      },
      {
        key: 'signal',
        label: 'Filter.amenities.signal',
        icon: 'Amenities/IconAmenitiesSignal',
      },
      {
        key: 'playground',
        label: 'Filter.amenities.playground',
        icon: 'Amenities/IconAmenitiesPlayground',
      },
      {
        key: 'pool',
        label: 'Filter.amenities.pool',
        icon: 'Amenities/IconAmenitiesPool',
      },
      {
        key: 'sauna',
        label: 'Filter.amenities.sauna',
        icon: 'Amenities/IconAmenitiesSauna',
      },
      {
        key: 'kidsFriendly',
        label: 'Filter.surroundings.kidsFriendly',
        icon: 'Amenities/IconSurroundingsKidsFriendly',
      },
    ],
  },
};

const carFilter = {
  id: 'kfz',
  label: 'Filter.kfz',
  type: 'SelectMultipleFilter',
  group: 'primary',
  queryParamNames: ['pub_kfz'],
  config: {
    searchMode: 'has_all',
    options: [
      { key: 'van', label: 'Filter.kfz.van', icon: 'Vehicles/IconVehiclesVan' },
      { key: 'bus', label: 'Filter.kfz.bus', icon: 'Vehicles/IconVehiclesBus' },
      { key: 'camper', label: 'Filter.kfz.camper', icon: 'Vehicles/IconVehiclesCamper' },
      { key: 'caravan', label: 'Filter.kfz.caravan', icon: 'Vehicles/IconVehiclesCaravan' },
      {
        key: 'motorhome',
        label: 'Filter.kfz.motorhome',
        icon: 'Vehicles/IconVehiclesMotorHome',
      },
      { key: 'carTent', label: 'Filter.kfz.carTent', icon: 'Vehicles/IconVehiclesCarTent' },
      { key: 'tent', label: 'Filter.kfz.tent', icon: 'Vehicles/IconVehiclesTent' },
    ],
  },
};

const earliestArrivalFilter = {
  id: 'earliestArrivalTime',
  label: 'Filter.earliestArrivalTime',
  type: 'TimeSelectFilter',
  group: 'secondary',
  queryParamNames: ['pub_earliest_arrival_time'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: '08_clock', label: 'Filter.earliestArrivalTime.08_clock' },
      { key: '09_clock', label: 'Filter.earliestArrivalTime.09_clock' },
      { key: '10_clock', label: 'Filter.earliestArrivalTime.10_clock' },
      { key: '11_clock', label: 'Filter.earliestArrivalTime.11_clock' },
      { key: '12_clock', label: 'Filter.earliestArrivalTime.12_clock' },
      { key: '13_clock', label: 'Filter.earliestArrivalTime.13_clock' },
      { key: '14_clock', label: 'Filter.earliestArrivalTime.14_clock' },
      { key: '15_clock', label: 'Filter.earliestArrivalTime.15_clock' },
      { key: '16_clock', label: 'Filter.earliestArrivalTime.16_clock' },
      { key: '17_clock', label: 'Filter.earliestArrivalTime.17_clock' },
      { key: '18_clock', label: 'Filter.earliestArrivalTime.18_clock' },
      { key: '19_clock', label: 'Filter.earliestArrivalTime.19_clock' },
      { key: '20_clock', label: 'Filter.earliestArrivalTime.20_clock' },
      { key: '21_clock', label: 'Filter.earliestArrivalTime.21_clock' },
    ],
  },
};

const latestArrivalFilter = {
  id: 'latestArrivalTime',
  label: 'Filter.latestArrivalTime',
  type: 'TimeSelectFilter',
  group: 'secondary',
  queryParamNames: ['pub_latest_arrival_time'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: '09_clock', label: 'Filter.latestArrivalTime.09_clock' },
      { key: '10_clock', label: 'Filter.latestArrivalTime.10_clock' },
      { key: '11_clock', label: 'Filter.latestArrivalTime.11_clock' },
      { key: '12_clock', label: 'Filter.latestArrivalTime.12_clock' },
      { key: '13_clock', label: 'Filter.latestArrivalTime.13_clock' },
      { key: '14_clock', label: 'Filter.latestArrivalTime.14_clock' },
      { key: '15_clock', label: 'Filter.latestArrivalTime.15_clock' },
      { key: '16_clock', label: 'Filter.latestArrivalTime.16_clock' },
      { key: '17_clock', label: 'Filter.latestArrivalTime.17_clock' },
      { key: '18_clock', label: 'Filter.latestArrivalTime.18_clock' },
      { key: '19_clock', label: 'Filter.latestArrivalTime.19_clock' },
      { key: '20_clock', label: 'Filter.latestArrivalTime.20_clock' },
      { key: '21_clock', label: 'Filter.latestArrivalTime.21_clock' },
      { key: '22_clock', label: 'Filter.latestArrivalTime.22_clock' },
    ],
  },
};

const latestDepartureFilter = {
  id: 'latestDepartureTime',
  label: 'Filter.latestDepartureTime',
  type: 'TimeSelectFilter',
  group: 'secondary',
  queryParamNames: ['pub_latest_departure_time'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: '08_clock', label: 'Filter.latestDepartureTime.08_clock' },
      { key: '09_clock', label: 'Filter.latestDepartureTime.09_clock' },
      { key: '10_clock', label: 'Filter.latestDepartureTime.10_clock' },
      { key: '11_clock', label: 'Filter.latestDepartureTime.11_clock' },
      { key: '12_clock', label: 'Filter.latestDepartureTime.12_clock' },
      { key: '13_clock', label: 'Filter.latestDepartureTime.13_clock' },
      { key: '14_clock', label: 'Filter.latestDepartureTime.14_clock' },
      { key: '15_clock', label: 'Filter.latestDepartureTime.15_clock' },
    ],
  },
};

const surroundingsFilter = {
  id: 'surroundings',
  label: 'Filter.surroundings',
  type: 'SelectMultipleFilter',
  group: 'primary',
  queryParamNames: ['pub_surroundings'],
  config: {
    searchMode: 'has_all',
    options: [
      {
        key: 'court',
        label: 'Filter.surroundings.court',
        icon: 'Surroundings/IconSurroundingsCourt',
      },
      {
        key: 'field',
        label: 'Filter.surroundings.field',
        icon: 'Surroundings/IconSurroundingsField',
      },
      {
        key: 'forest',
        label: 'Filter.surroundings.forest',
        icon: 'Surroundings/IconSurroundingsForest',
      },
      {
        key: 'lake',
        label: 'Filter.surroundings.lake',
        icon: 'Surroundings/IconSurroundingsLake',
      },
      {
        key: 'meadow',
        label: 'Filter.surroundings.meadow',
        icon: 'Surroundings/IconSurroundingsMeadow',
      },
      {
        key: 'sea',
        label: 'Filter.surroundings.sea',
        icon: 'Surroundings/IconSurroundingsSea',
      },
      {
        key: 'mountain',
        label: 'Filter.surroundings.mountain',
        icon: 'Surroundings/IconSurroundingsMountain',
      },
      {
        key: 'river',
        label: 'Filter.surroundings.river',
        icon: 'Surroundings/IconSurroundingsRiver',
      },
      {
        key: 'house',
        label: 'Filter.surroundings.house',
        icon: 'Surroundings/IconSurroundingsHouse',
      },
    ],
  },
};

const activitiesFilter = {
  id: 'activities',
  label: 'Filter.activities',
  type: 'SelectMultipleFilter',
  group: 'primary',
  queryParamNames: ['pub_activities'],
  config: {
    searchMode: 'has_all',
    options: [
      {
        key: 'alpaca',
        label: 'Filter.activities.alpaca',
        icon: 'Activities/IconActivityAlpaca',
      },
      {
        key: 'climbing',
        label: 'Filter.activities.climbing',
        icon: 'Activities/IconActivityClimbing',
      },
      {
        key: 'coworking',
        label: 'Filter.activities.coworking',
        icon: 'Activities/IconActivityCoworking',
      },
      {
        key: 'cycling',
        label: 'Filter.activities.cycling',
        icon: 'Activities/IconActivityCycling',
      },
      {
        key: 'fishing',
        label: 'Filter.activities.fishing',
        icon: 'Activities/IconActivityFishing',
      },
      {
        key: 'hiking',
        label: 'Filter.activities.hiking',
        icon: 'Activities/IconActivityHiking',
      },
      {
        key: 'paddle',
        label: 'Filter.activities.paddle',
        icon: 'Activities/IconActivityPaddle',
      },
      {
        key: 'quad_tour',
        label: 'Filter.activities.quad_tour',
        icon: 'Activities/IconActivityQuadTour',
      },
      {
        key: 'riding',
        label: 'Filter.activities.riding',
        icon: 'Activities/IconActivityRiding',
      },
      {
        key: 'sailing',
        label: 'Filter.activities.sailing',
        icon: 'Activities/IconActivitySailing',
      },
      {
        key: 'sightseeing_flight',
        label: 'Filter.activities.sightseeing_flight',
        icon: 'Activities/IconActivitySightseeingFlight',
      },
      {
        key: 'skydiving',
        label: 'Filter.activities.skydiving',
        icon: 'Activities/IconActivitySkydiving',
      },

      {
        key: 'surfing',
        label: 'Filter.activities.surfing',
        icon: 'Activities/IconActivitySurfing',
      },
      {
        key: 'swimming',
        label: 'Filter.activities.swimming',
        icon: 'Activities/IconActivitySwimming',
      },
      {
        key: 'tennis',
        label: 'Filter.activities.tennis',
        icon: 'Activities/IconActivityTennis',
      },
      {
        key: 'wake_boarding',
        label: 'Filter.activities.wake_boarding',
        icon: 'Activities/IconActivityWakeBoarding',
      },
      {
        key: 'wellness',
        label: 'Filter.activities.wellness',
        icon: 'Activities/IconActivityWellness',
      },
      {
        key: 'wine_tasting',
        label: 'Filter.activities.wineTasting',
        icon: 'Activities/IconActivityWineTasting',
      },
    ],
  },
};

const priceFilter = {
  id: 'price',
  label: 'Filter.price',
  type: 'PriceFilter',
  group: 'primary',
  // Note: PriceFilter is fixed filter,
  // you can't change "queryParamNames: ['price'],"
  queryParamNames: ['price'],
  // Price filter configuration
  // Note: unlike most prices this is not handled in subunits
  config: {
    min: 0,
    max: 100,
    step: 5,
  },
};

const keywordFilter = {
  id: 'keyword',
  label: 'Filter.keyword',
  type: 'KeywordFilter',
  group: 'secondary',
  // Note: KeywordFilter is fixed filter,
  // you can't change "queryParamNames: ['keywords'],"
  queryParamNames: ['keywords'],
  // NOTE: If you are ordering search results by distance
  // the keyword search can't be used at the same time.
  // You can turn on/off ordering by distance from config.js file.
  config: {},
};

const responseTimeFilter = {
  id: 'responseTime',
  label: 'Filter.responseTime',
  type: 'SelectMultipleFilter',
  group: 'secondary',
  queryParamNames: ['pub_response_time'],
  config: {
    searchMode: 'has_any',
    options: [
      { key: '72h', label: 'Filter.responseTime.72h' },
      { key: '48h', label: 'Filter.responseTime.48h' },
      { key: '24h', label: 'Filter.responseTime.24h' },
      { key: '12h', label: 'Filter.responseTime.12h' },
    ],
  },
};

const cancellationPolicyFilter = {
  id: 'cancellationPolicy',
  label: 'Filter.cancellationPolicy',
  type: '',
  group: 'secondary',
  queryParamNames: ['pub_cancellation_policy'],
  config: {
    options: {
      topLimit: {
        options: [
          { key: '0', label: '0%' },
          { key: '10', label: '10%' },
          { key: '20', label: '20%' },
          { key: '30', label: '30%' },
          { key: '40', label: '40%' },
          { key: '50', label: '50%' },
          { key: '60', label: '60%' },
          { key: '70', label: '70%' },
          { key: '80', label: '80%' },
          { key: '90', label: '90%' },
          { key: '100', label: '100%' },
        ],
      },
      middleLimit: {
        options: [
          { key: '0', label: '0%' },
          { key: '10', label: '10%' },
          { key: '20', label: '20%' },
          { key: '30', label: '30%' },
          { key: '40', label: '40%' },
          { key: '50', label: '50%' },
          { key: '60', label: '60%' },
          { key: '70', label: '70%' },
          { key: '80', label: '80%' },
          { key: '90', label: '90%' },
          { key: '100', label: '100%' },
        ],
      },
      bottomLimit: {
        options: [
          { key: '0', label: '0%' },
          { key: '10', label: '10%' },
          { key: '20', label: '20%' },
          { key: '30', label: '30%' },
          { key: '40', label: '40%' },
          { key: '50', label: '50%' },
          { key: '60', label: '60%' },
          { key: '70', label: '70%' },
          { key: '80', label: '80%' },
          { key: '90', label: '90%' },
          { key: '100', label: '100%' },
        ],
      },
    },
  },
};

const cancellationPolicyTierFilter = {
  id: 'cancellationPolicyTier',
  label: 'Filter.cancellationPolicyTier',
  type: 'SelectMultipleFilter',
  group: 'secondary',
  queryParamNames: ['pub_cancellation_policy'],
  config: {
    options: [
      { key: 'flexible', label: 'Filter.cancellationPolicyTier.flexible', icon: 'IconClose' },
      { key: 'moderate', label: 'Filter.cancellationPolicyTier.moderate', icon: 'IconClose' },
      { key: 'strict', label: 'Filter.cancellationPolicyTier.strict', icon: 'IconClose' },
    ],
  },
};

export const filters = [
  bookingCategoryFilter,
  verifiedFilter,

  noPlusListingFilter,

  allWheelDriveFilter,

  listingCategoryFilter,
  amenitiesFilter,
  carFilter,

  surroundingsFilter,
  activitiesFilter,

  earliestArrivalFilter,
  latestArrivalFilter,

  latestDepartureFilter,

  dateFilter,
  priceFilter,
  keywordFilter,

  responseTimeFilter,
  cancellationPolicyFilter,
  cancellationPolicyTierFilter,
  bookingLimit,
  bookingMinimum,
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Keyword filter is sorting the results already by relevance.
  // If keyword filter is active, we need to disable sorting.
  conflictingFilters: ['keyword'],

  options: [
    { key: 'pub_verified,-price', label: 'SortConfig.-price' },
    { key: 'pub_verified,price', label: 'SortConfig.price' },
    { key: '-meta_rating,meta_ratingCount', label: 'SortConfig.-rating' },
    { key: 'meta_rating,meta_ratingCount', label: 'SortConfig.rating' },
    { key: '-createdAt', label: 'SortConfig.-createdAt' },
    { key: 'createdAt', label: 'SortConfig.createdAt' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    {
      key: 'relevance',
      label: 'SortConfig.relevance',
      longLabel: 'SortConfig.relevance.longLabel',
    },
  ],
};
